import { Inject, Injectable, OnDestroy } from '@angular/core';
import { SubscriptionList } from '@vendasta/rx-utils';
import { getStates, ObservableWorkStateMap, WorkStateMap } from '@vendasta/rx-utils/work-state';
import { Pipeline } from '@vendasta/sales-common';
import {
  DateRangeFilter,
  ListOpportunitiesFilters,
  ListOpportunitiesRequestSortField as SortField,
  ListOpportunitiesRequestSortOptionInterface as SortOption,
  ListOpportunitiesResponse,
  Opportunity,
  OpportunityRevenuePeriod,
  RevenuePeriodFilter,
  SalesOpportunitiesSdk,
  SalesOpportunitiesService,
  SortDirection,
  StageInterface,
} from '@vendasta/sales-opportunities';
import { DateRange } from '@vendasta/uikit';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { first, map, scan, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AccountFilterService } from '../../filters/account-filter/account-filter.service';
import { PipelineFiltersService } from '../../filters/filters-service/pipeline-filters.service';
import { OpportunitiesCommands } from '../../sales-opportunities/interface';
import { OpportunityService } from '../../sales-opportunities/opportunity.service';
import { LockMap } from '../../utils';
import { PipelineBusinessNameService } from '../pipeline-business-name/pipeline-business-name.service';
import { buildSortOptions, convertSortOptionToPipelineSort, PipelineSort } from '../pipeline-sort';
import { PipelineStats } from '../pipeline-stats/pipeline-stats.service';
import { PipelineStatuses } from '../pipeline-status-checkbox/pipeline-status-checkbox.service';
import { PipelineServiceInterface, PipelineUIFilters } from '../pipeline/pipeline-service.interface';
import { PotentialRevenueService } from '../pipeline/potential-revenue/potential-revenue.service';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../tokens';
import { DEFAULT_PIPELINE_ID, UserPipelines } from '../user-pipelines/user-pipelines';
import { PipelineBoardAction, PipelineBoardEvent, PipelineBoardFilter, SortSelection } from './interfaces';
import { SALESPERSON_ID_TOKEN } from './tokens';

export const INITIAL_SORT: SORT_DIRECTIONS = true;
export type SORT_DIRECTIONS = boolean;

function pipelineStatusesToListOpportunitiesRequestFilter(statuses: PipelineStatuses): string[] {
  const out = [];
  if (statuses?.open) {
    out.push('open');
  }
  if (statuses?.won) {
    out.push('closed-won');
  }
  if (statuses?.lost) {
    out.push('closed-lost');
  }
  return out;
}

export function toBoardFilter(filters: PipelineUIFilters): PipelineBoardFilter {
  const boardFilter = <PipelineBoardFilter>{};

  boardFilter.pipelineStages = pipelineStatusesToListOpportunitiesRequestFilter(filters.statuses);
  boardFilter.expectedCloseDateRange = dateRangeToDateRangeFilter(filters.expectedClose);
  boardFilter.actualCloseDateRange = dateRangeToDateRangeFilter(filters.actualClose);
  boardFilter.createdDateRange = dateRangeToDateRangeFilter(filters.createdDate);
  boardFilter.assignedDateRange = dateRangeToDateRangeFilter(filters.assignedDate);

  boardFilter.revenuePeriodFilter = filters.monthlyRevenueDate
    ? <RevenuePeriodFilter>{
        revenuePeriod: OpportunityRevenuePeriod.SELECTED_MONTH,
        revenueDate: filters.monthlyRevenueDate,
      }
    : <RevenuePeriodFilter>{ revenuePeriod: null, revenueDate: null };
  boardFilter.pipelineId = filters.pipelines?.length > 0 ? filters.pipelines[0].id : '';
  boardFilter.salesPersonIds = filters.salespeople?.map((s) => s.id) || [];
  boardFilter.salesTeamMemberIds = filters.salesTeamSalespersonIds || [];
  boardFilter.oppTags = filters.oppTags?.map((o) => o.value) || [];
  boardFilter.accountTags = filters.acctTags?.map((o) => o.value) || [];
  boardFilter.packageIds = filters.packages?.map((p) => p.packageId) || [];
  boardFilter.lineItemProductIds = filters.products?.map((p) => p.productId) || [];
  boardFilter.opportunityTypes = filters.types?.map((o) => o.id) || [];
  return boardFilter;
}

function dateRangeToDateRangeFilter(dateRange: DateRange): DateRangeFilter {
  return dateRange ? <DateRangeFilter>{ beginRange: dateRange.start, endRange: dateRange.end } : null;
}

@Injectable()
export class PipelineBoardService implements OpportunitiesCommands, PipelineServiceInterface, OnDestroy {
  private readonly subscriptions = SubscriptionList.new();

  public readonly loadingLockMap = new LockMap();
  private salespersonId: string;
  private readonly opportunitiesPerPage = 100;
  private readonly stageOpportunitiesWorkStateMap = new ObservableWorkStateMap<string, Opportunity[]>();
  private readonly cursorsByStage = new Map<string, BehaviorSubject<string>>();
  private readonly salespersonSelections$$ = new ReplaySubject<string[]>(1);
  readonly salespersonSelections$: Observable<string[]>;
  private readonly defaultSortDirection$$ = new BehaviorSubject<SORT_DIRECTIONS>(INITIAL_SORT);
  readonly defaultSortDirection$ = this.defaultSortDirection$$.asObservable();
  private readonly pipelineBoardEvent$$ = new ReplaySubject<PipelineBoardEvent>(1);
  readonly pipelineBoardEvent$: Observable<PipelineBoardEvent>;
  private readonly pipelineBoardAction$$ = new ReplaySubject<PipelineBoardAction>(1);
  readonly pipelineBoardAction$: Observable<PipelineBoardAction>;
  private readonly sortSelection$$ = new BehaviorSubject<SortSelection>(<SortSelection>{ potentialRevenue: true });
  readonly sortSelection$: Observable<SortSelection>;
  private readonly currentPipelineFilter$$ = new BehaviorSubject<PipelineBoardFilter>(<PipelineBoardFilter>{});
  readonly currentPipelineFilter$ = this.currentPipelineFilter$$.asObservable();
  private readonly triggerBoardReload$$ = new Subject<void>();
  readonly triggerBoardReload$ = this.triggerBoardReload$$.asObservable().pipe(startWith({}));
  readonly currentPipeline$ = this.userPipelines.getCurrentPipeline();

  // Workaround to make sure revenue dropdown is in sync when revenue date changed on pipeline table
  private readonly selectedRevenueDate$$ = new BehaviorSubject<Date>(null);

  SORT_DEFAULTS_ARRAY: SortOption[] = [
    { field: SortField.PROJECTED_FIRST_YEAR_VALUE, direction: SortDirection.DESCENDING },
    { field: SortField.EXPECTED_CLOSE, direction: SortDirection.DESCENDING },
    { field: SortField.ACTUAL_CLOSE, direction: SortDirection.DESCENDING },
    { field: SortField.LAST_CONNECTED, direction: SortDirection.ASCENDING },
    { field: SortField.LAST_ACTIVITY, direction: SortDirection.ASCENDING },
    { field: SortField.NAME, direction: SortDirection.ASCENDING },
    { field: SortField.CREATED, direction: SortDirection.DESCENDING },
    { field: SortField.ASSIGNED_DATE, direction: SortDirection.DESCENDING },
    { field: SortField.MEDDIC, direction: SortDirection.DESCENDING },
  ];
  currentSortOption$$ = new BehaviorSubject<SortOption>(this.SORT_DEFAULTS_ARRAY[0]);
  currentSortField$$ = new BehaviorSubject<SortField>(this.SORT_DEFAULTS_ARRAY[0].field);

  lastSearchTerm$: Observable<string> = of('');
  sort$ = this.currentSortOption$$.pipe(map((o) => convertSortOptionToPipelineSort(o)));

  selectedUIFilters$: Observable<PipelineUIFilters> = this.pipelineFiltersService.selectedUIFilters$;

  readonly updateOpportunityWorkState: WorkStateMap<string, Opportunity>;
  readonly stageState = (stageId: string) => getStates(this.stageOpportunitiesWorkStateMap, stageId);

  constructor(
    private readonly updates: OpportunityService,
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
    private readonly userPipelines: UserPipelines,
    private readonly pipelineStats: PipelineStats,
    private readonly businessNameService: PipelineBusinessNameService,
    private readonly potentialRevenueService: PotentialRevenueService,
    private readonly pipelineFiltersService: PipelineFiltersService,
    private readonly accountFilterService: AccountFilterService,
    @Inject(SALESPERSON_ID_TOKEN) private readonly salespersonId$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
  ) {
    this.sortSelection$ = this.sortSelection$$.asObservable();
    this.pipelineBoardEvent$ = this.pipelineBoardEvent$$.asObservable();
    this.pipelineBoardAction$ = this.pipelineBoardAction$$.asObservable();
    this.salespersonSelections$ = this.salespersonSelections$$.asObservable();

    this.subscriptions.add(this.pipelineStats.totalRevenueForAllStages$, (revenue) => {
      this.potentialRevenueService.setPotentialRevenue(revenue);
    });

    this.subscriptions.add(this.pipelineStats.totalWeightedRevenueForAllStages$, (revenue) => {
      this.potentialRevenueService.setWeightedRevenue(revenue);
    });

    this.subscriptions.add(this.pipelineStats.totalRevenueInPeriodForAllStages$, (revenueInPeriod) => {
      this.potentialRevenueService.setMonthlyRevenue(revenueInPeriod);
    });

    this.subscriptions.add(this.pipelineStats.totalWeightedRevenueInPeriodForAllStages$, (revenueInPeriod) => {
      this.potentialRevenueService.setMonthlyWeightedRevenue(revenueInPeriod);
    });

    this.potentialRevenueService.selectedDate$.subscribe(this.selectedRevenueDate$$);
    this.potentialRevenueService.selectedDate$.subscribe((selectedDate) => {
      this.setPotentialRevenueFilter(selectedDate);
    });

    this.subscriptions.add(this.accountFilterService.selectedAccountGroupId$, (accountGroupId) =>
      this.setAccountGroupFilter(accountGroupId),
    );

    this.updateOpportunityWorkState = this.updates.state;

    combineLatest([this.salespersonId$, this.currentPipeline$, this.triggerBoardReload$])
      .pipe(
        withLatestFrom(this.currentPipelineFilter$),
        tap(([[salespersonId, pipeline], currentFilter]) => {
          this.salespersonId = salespersonId;
          for (const s of pipeline.stages) {
            if (!this.cursorsByStage.has(s.stageId)) {
              this.cursorsByStage.set(s.stageId, new BehaviorSubject<string>(null));
            }
          }
          this.initPipelineBoardActionAndEvent(pipeline, salespersonId, currentFilter);
        }),
      )
      .subscribe();

    this.selectedUIFilters$
      .pipe(
        withLatestFrom(this.currentPipeline$),
        tap(([filters, p]) => {
          this.pipelineBoardEvent$$.next({
            eventType: 'filterOpportunities',
            filters: toBoardFilter(filters),
            pipeline: p,
          });
        }),
      )
      .subscribe();

    this.defaultSortDirection$$
      .pipe(withLatestFrom(this.currentSortField$$))
      .subscribe(([direction, sortField]) => this.sortActions(sortField, direction));
    this.currentSortField$$
      .pipe(withLatestFrom(this.defaultSortDirection$$))
      .subscribe(([sortField, defaultDirection]) => this.sortActions(sortField, defaultDirection));
  }

  applyFiltersFromQueryParams(): void {
    this.pipelineFiltersService.applyFiltersFromQueryParams();
  }

  initPipelineBoardActionAndEvent(pipeline: Pipeline, salespersonId: string, currentFilter: PipelineBoardFilter): void {
    currentFilter.salesPersonIds = currentFilter.salesPersonIds || (salespersonId ? [salespersonId] : []);
    currentFilter.pipelineStages = currentFilter.pipelineStages || ['open'];
    currentFilter.salesTeamMemberIds = currentFilter.salesTeamMemberIds || [];
    currentFilter.pipelineId = pipeline.id;

    this.pipelineBoardEvent$$.next({
      eventType: 'loadOpportunities',
      pipeline: pipeline,
      filters: currentFilter,
    });

    this.pipelineBoardAction$$.next({
      sortOption: this.currentSortOption$$.getValue(),
      filters: currentFilter,
    });
  }

  setCurrentPipeline(pipelineID: string): void {
    this.cursorsByStage.forEach((c) => c.next(''));
    this.userPipelines.setCurrentPipelineID(pipelineID);
  }

  reloadBoard(): void {
    this.triggerBoardReload$$.next();
  }

  loadOpportunitiesInStage(stage: StageInterface): Observable<Opportunity[]> {
    return this.pipelineBoardEvent$.pipe(
      scan((action, event) => {
        action.pipeline = event.pipeline;
        switch (event.eventType) {
          case 'loadOpportunities':
            action.filters = Object.assign(action.filters, event.filters);
            this.doListFor(stage, event.pipeline, action.sortOption, action.filters);
            break;
          case 'sortOpportunities':
            action.sortOption = event.sortOption;
            this.doListFor(stage, event.pipeline, action.sortOption, action.filters);
            break;
          case 'filterOpportunities':
            action.filters = Object.assign(action.filters, event.filters);
            this.doListFor(stage, event.pipeline, action.sortOption, action.filters);
            break;
          case 'loadMoreOpportunities':
            if (event.cursor !== null && event.cursor !== undefined) {
              if (stage === event.stage) {
                this.getNextPageWithCursor(
                  event.stage,
                  event.cursor,
                  event.pipeline,
                  action.sortOption,
                  action.filters,
                );
                if (event.isLoading === false) {
                  this.loadingLockMap.unlock(stage.stageId);
                }
              }
            }
        }
        this.pipelineBoardAction$$.next(action);
        this.currentPipelineFilter$$.next(action.filters);
        return action;
      }, this.getOpportunityActionSeed()),
      withLatestFrom(this.selectedRevenueDate$$),
      tap(([action, revenueDate]) => {
        if (revenueDate !== action.filters.revenuePeriodFilter.revenueDate) {
          this.potentialRevenueService.setDate(revenueDate);
        }
      }),
      switchMap(([action]) => this.applyOpportunities(stage, action.pipeline, action.sortOption)),
    );
  }

  getOpportunityActionSeed(): PipelineBoardAction {
    return <PipelineBoardAction>{
      sortOption: this.currentSortOption$$.getValue(),
      filters: {
        salesPersonIds:
          this.currentPipelineFilter$$.getValue().salesPersonIds || (this.salespersonId ? [this.salespersonId] : []),
        salesTeamMemberIds: this.currentPipelineFilter$$.getValue().salesTeamMemberIds || [],
        pipelineStages: this.currentPipelineFilter$$.getValue().pipelineStages || ['open'],
        expectedCloseDateRange: this.currentPipelineFilter$$.getValue()?.expectedCloseDateRange || null,
        actualCloseDateRange: this.currentPipelineFilter$$.getValue()?.actualCloseDateRange || null,
        createdDateRange: this.currentPipelineFilter$$.getValue()?.createdDateRange || null,
        assignedDateRange: this.currentPipelineFilter$$.getValue()?.assignedDateRange || null,
        revenuePeriodFilter:
          this.currentPipelineFilter$$.getValue().revenuePeriodFilter ??
          new RevenuePeriodFilter({
            revenueDate: null,
            revenuePeriod: OpportunityRevenuePeriod.UNSPECIFIED,
          }),
      },
    };
  }

  updateProbability(opportunity: Opportunity, probability: number): void {
    this.updates.updateProbability(opportunity, probability);
  }

  updatePipelineId(opportunity: Opportunity, newPipelineId: string, newProbability: number): void {
    this.updates.updatePipelineId(opportunity, newPipelineId, newProbability);
  }

  opportunitiesInStage$(stage: StageInterface): Observable<Opportunity[]> {
    return this.currentPipeline$.pipe(
      switchMap((pipeline) => {
        return this.loadOpportunitiesInStage(stage).pipe(
          map((opps) => opps.filter((opp) => opp.pipelineId === pipeline.id)),
        );
      }),
    );
  }

  cursorForStage$(stageId: string): Observable<string> {
    return this.cursorsByStage.get(stageId).asObservable();
  }

  applyOpportunities(stage: StageInterface, pipeline: Pipeline, option?: SortOption): Observable<Opportunity[]> {
    const opportunities = this.stageOpportunitiesWorkStateMap.getWorkResults$(stage.stageId);
    const range = pipeline.findProbabilityRangeFromStage(stage);
    return this.updates.applyForStage$(opportunities, range.minimum, range.maximum, option);
  }

  private getStageForOpportunity(opportunity: Opportunity, pipeline: Pipeline): StageInterface {
    return pipeline.stages.find((stage) => {
      const range = pipeline.findProbabilityRangeFromStage(stage);
      return opportunity.probability >= range.minimum && opportunity.probability <= range.maximum;
    });
  }

  public addOpportunity(opportunity: Opportunity): void {
    this.currentPipeline$.pipe(take(1)).subscribe((pipeline) => {
      const stage = this.getStageForOpportunity(opportunity, pipeline);
      if (!stage) {
        return;
      }

      // Get the current opportunities for the determined stage and update the work state map
      this.stageOpportunitiesWorkStateMap
        .getWorkResults$(stage.stageId)
        .pipe(take(1))
        .subscribe((currentOpportunities) => {
          const updatedOpportunities = currentOpportunities ? [...currentOpportunities, opportunity] : [opportunity];
          this.stageOpportunitiesWorkStateMap.startWork(stage.stageId, of(updatedOpportunities));
        });
    });
  }

  public updateOpportunity(updatedOpp: Opportunity): void {
    this.currentPipeline$.pipe(take(1)).subscribe((pipeline) => {
      const targetStage = this.getStageForOpportunity(updatedOpp, pipeline);
      if (!targetStage) {
        return;
      }

      // Retrieve the current opportunities for the target stage.
      this.stageOpportunitiesWorkStateMap
        .getWorkResults$(targetStage.stageId)
        .pipe(take(1))
        .subscribe((currentOpportunities = []) => {
          // Find the index of the updated opportunity in the current list.
          const index = currentOpportunities.findIndex((o) => o.opportunityId === updatedOpp.opportunityId);
          let updatedList: Opportunity[];

          if (index >= 0) {
            // Replace the existing opportunity.
            updatedList = [...currentOpportunities];
            updatedList[index] = updatedOpp;
          } else {
            // If not found (for example, if the opportunity changed stages),
            // add the updated opportunity to the list.
            updatedList = [...currentOpportunities, updatedOpp];
          }

          // Update the work state map to reflect the change.
          this.stageOpportunitiesWorkStateMap.startWork(targetStage.stageId, of(updatedList));
        });
    });
  }

  public getNextPageWithCursor(
    stage: StageInterface,
    cursor: string,
    pipeline: Pipeline,
    sortOption: SortOption,
    filters: PipelineBoardFilter,
  ): void {
    this.triggerLoadNextPage(stage, pipeline, sortOption, filters, cursor);
    this.applyOpportunities(stage, pipeline, sortOption);
  }

  public triggerLoadNextPage(
    stage: StageInterface,
    pipeline: Pipeline,
    sortOption: SortOption,
    filters: PipelineBoardFilter,
    cursor?: string,
  ): void {
    if (cursor !== null && cursor !== undefined) {
      this.stageOpportunitiesWorkStateMap
        .getWorkResults$(stage.stageId)
        .pipe(first())
        .subscribe((currentOpportunities) => {
          this.doListFor(stage, pipeline, sortOption, filters, currentOpportunities, cursor);
        });
    } else {
      this.doListFor(stage, pipeline, sortOption, filters);
    }
  }

  private doListFor(
    stage: StageInterface,
    pipeline: Pipeline,
    sortOption: SortOption,
    filters: PipelineBoardFilter,
    currentOpportunities?: Opportunity[],
    cursor?: string,
  ): void {
    const range = pipeline.findProbabilityRangeFromStage(stage);
    const updatedFilter: ListOpportunitiesFilters = {
      probabilityRangeFilter: range,
      salesPersonIds: [...new Set(filters.salesPersonIds.concat(filters.salesTeamMemberIds))],
      pipelineStages: filters.pipelineStages,
      pipelineId: DEFAULT_PIPELINE_ID,
      packageIds: filters.packageIds,
      tags: (filters.oppTags || []).concat(filters.accountTags || []),
      expectedCloseDateFilter: filters.expectedCloseDateRange,
      actualCloseDateFilter: filters.actualCloseDateRange,
      createdDateFilter: filters.createdDateRange,
      assignedDate: filters.assignedDateRange,
      lineItemProductIds: filters.lineItemProductIds,
      revenuePeriodFilter: filters.revenuePeriodFilter,
      accountGroupId: filters.accountGroupId,
      opportunityTypes: filters.opportunityTypes,
    };

    if (pipeline.id !== DEFAULT_PIPELINE_ID) {
      updatedFilter.pipelineId = pipeline.id;
    }

    const listOpportunityResponse = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) => {
        return this.opportunityApi.list(
          partnerId,
          marketId,
          updatedFilter,
          sortOption,
          '',
          this.opportunitiesPerPage,
          cursor || null,
        );
      }),
    );

    listOpportunityResponse.pipe(take(1)).subscribe((response: ListOpportunitiesResponse) => {
      this.pipelineStats.setStatsForStage(stage.stageId, {
        revenue: response.totalProjectedFirstYearValue ?? 0,
        weightedRevenue: (response.totalProjectedFirstYearValue ?? 0) * (stage.probability ?? 1),
        revenueInPeriod: response.totalRevenueForRevenuePeriod ?? 0,
        weightedRevenueInPeriod: (response.totalRevenueForRevenuePeriod ?? 0) * (stage.probability ?? 1),
        totalOpportunities: response.totalResults ?? 0,
      });
      this.cursorsByStage.get(stage.stageId).next(response.nextCursor || null);
      const opps =
        currentOpportunities !== undefined ? currentOpportunities.concat(response.results) : response.results || [];
      const agIds = [...new Set(opps.map((opp) => opp.accountGroupId))];
      this.businessNameService.enqueueAccountLoad(agIds);
      this.loadingLockMap.unlock(stage.stageId);
      this.stageOpportunitiesWorkStateMap.startWork(stage.stageId, of(opps));
    });
  }

  doGetNextPageFor(stage: StageInterface): void {
    if (this.loadingLockMap.isLocked(stage.stageId)) {
      return;
    }
    this.loadingLockMap.lock(stage.stageId);

    combineLatest([
      this.cursorForStage$(stage.stageId),
      this.stageState(stage.stageId).isLoading$,
      this.currentPipeline$,
    ])
      .pipe(
        first(),
        tap(([cursor, isLoading, pipeline]) => {
          this.pipelineBoardEvent$$.next({
            eventType: 'loadMoreOpportunities',
            stage: stage,
            cursor: cursor,
            isLoading: isLoading,
            pipeline: pipeline,
          });
        }),
      )
      .subscribe();
  }

  doLoadMoreIfNecessary(arraySize: number, stage: StageInterface): void {
    if (arraySize <= this.opportunitiesPerPage) {
      this.doGetNextPageFor(stage);
    }
  }

  setFilters(filters?: PipelineBoardFilter): void {
    this.currentPipeline$.pipe(take(1)).subscribe((pipeline) => {
      this.pipelineBoardEvent$$.next({
        eventType: 'filterOpportunities',
        filters: filters,
        pipeline: pipeline,
      });
    });
  }

  sortActions(field?: SortField, defaultDirection?: SORT_DIRECTIONS): void {
    const sortSelected = this.SORT_DEFAULTS_ARRAY.find((sortFields) => sortFields.field === field);
    const sortConfig = <SortSelection>{};
    const sortOption = <SortOption>{
      field: sortSelected ? sortSelected.field : this.currentSortOption$$.getValue().field,
      direction: sortSelected
        ? this.getFieldSpecificSortDirection(sortSelected.direction, defaultDirection)
        : this.currentSortOption$$.getValue().direction,
    };
    switch (sortOption.field) {
      case SortField.EXPECTED_CLOSE:
        sortConfig.expectedCloseDate = true;
        break;
      case SortField.ACTUAL_CLOSE:
        sortConfig.actualCloseDate = true;
        break;
      case SortField.LAST_CONNECTED:
        sortConfig.lastConnected = true;
        break;
      case SortField.LAST_ACTIVITY:
        sortConfig.lastSalesActivity = true;
        break;
      case SortField.PROJECTED_FIRST_YEAR_VALUE:
        sortConfig.potentialRevenue = true;
        break;
      case SortField.NAME:
        sortConfig.name = true;
        break;
      case SortField.CREATED:
        sortConfig.createdDate = true;
        break;
      case SortField.ASSIGNED_DATE:
        sortConfig.assignedDate = true;
        break;
      case SortField.MEDDIC:
        sortConfig.meddic = true;
        break;
      default:
        sortConfig.potentialRevenue = true;
        break;
    }

    this.currentPipeline$.pipe(take(1)).subscribe((pipeline) => {
      this.pipelineBoardEvent$$.next({
        eventType: 'sortOpportunities',
        sortOption: sortOption,
        pipeline: pipeline,
      });

      this.currentSortOption$$.next(sortOption);
      this.sortSelection$$.next(sortConfig);
    });
  }

  getFieldSpecificSortDirection(direction: SortDirection, useDefaultSortDirection: SORT_DIRECTIONS): SortDirection {
    if (!useDefaultSortDirection) {
      return direction === SortDirection.DESCENDING ? SortDirection.ASCENDING : SortDirection.DESCENDING;
    }
    return direction;
  }

  resetSortDirection(): SORT_DIRECTIONS {
    return this.setSortDirection(INITIAL_SORT);
  }

  reverseSortDirection(): SORT_DIRECTIONS {
    const newSortDirection = !this.defaultSortDirection$$.getValue();
    return this.setSortDirection(newSortDirection);
  }

  private setSortDirection(sortDirection: SORT_DIRECTIONS): SORT_DIRECTIONS {
    this.defaultSortDirection$$.next(sortDirection);
    return sortDirection;
  }

  sortByExpectedClose(): void {
    this.setSort(SortField.EXPECTED_CLOSE);
  }

  sortByActualClose(): void {
    this.setSort(SortField.ACTUAL_CLOSE);
  }

  sortByCreatedDate(): void {
    this.setSort(SortField.CREATED);
  }

  sortByAssignedDate(): void {
    this.setSort(SortField.ASSIGNED_DATE);
  }

  clearExpectedClose(): void {
    if (this.currentSortField$$.getValue() === SortField.EXPECTED_CLOSE) {
      this.currentSortField$$.next(SortField.PROJECTED_FIRST_YEAR_VALUE);
    }
  }

  clearActualClose(): void {
    if (this.currentSortField$$.getValue() === SortField.ACTUAL_CLOSE) {
      this.currentSortField$$.next(SortField.PROJECTED_FIRST_YEAR_VALUE);
    }
  }

  clearCreatedDate(): void {
    if (this.currentSortField$$.getValue() === SortField.CREATED) {
      this.currentSortField$$.next(SortField.PROJECTED_FIRST_YEAR_VALUE);
    }
  }

  clearAssignedDate(): void {
    if (this.currentSortField$$.getValue() === SortField.ASSIGNED_DATE) {
      this.currentSortField$$.next(SortField.PROJECTED_FIRST_YEAR_VALUE);
    }
  }

  setFilterAndSort(boardFilter: PipelineBoardFilter, sortField: SortField): void {
    this.setFilters(boardFilter);
    this.setSort(sortField);
  }

  setSort(sortField: SortField): void {
    if (this.defaultSortDirection$$.getValue() !== INITIAL_SORT) {
      this.resetSortDirection();
    }
    if (this.currentSortField$$.getValue() !== sortField) {
      this.currentSortField$$.next(sortField);
    }
  }

  setSelectedSalespeople(sp: string[]): void {
    const filters = <PipelineBoardFilter>{
      salesPersonIds: sp,
    };
    this.setFilters(filters);
  }

  private setPotentialRevenueFilter(selectedDate: Date): void {
    this.setFilters(<PipelineBoardFilter>{
      revenuePeriodFilter: new RevenuePeriodFilter({
        revenueDate: selectedDate,
        revenuePeriod: selectedDate ? OpportunityRevenuePeriod.SELECTED_MONTH : OpportunityRevenuePeriod.UNSPECIFIED,
      }),
    });
  }

  private setAccountGroupFilter(accountGroupId: string): void {
    const filters = <PipelineBoardFilter>{
      accountGroupId: accountGroupId,
    };
    this.setFilters(filters);
  }

  private setOpportunityTypesFilter(opportunityTypes: string[]): void {
    const filters = <PipelineBoardFilter>{
      opportunityTypes: opportunityTypes,
    };
    this.setFilters(filters);
  }

  applySavedFiltersFromStorage(searchTerm: string, filters: PipelineUIFilters, sort: PipelineSort): void {
    this.pipelineFiltersService.applySavedFiltersFromStorage(filters);
    const sortOptions = buildSortOptions(sort);

    this.setSort(sortOptions.field);
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }
}
